import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { DateTimeService } from 'i2g-ng-utils';
import { List } from 'immutable';
import { noop } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  finalize,
  flatMap,
  map,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { v4 } from 'uuid';
import { ClientDataDispatcher } from '../../../client-data/client-data.dispatcher';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogService } from '../../../dialog/dialog.service';
import {
  DOCUMENTS_NEW_ROUTE,
  DOCUMENTS_PREVIEW_ROUTE,
} from '../../../document-data/document-data.data';
import { IDocumentStore } from '../../../document-data/document-store.interface';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { PaymentTransaction } from '../../../document-data/sub-models/payment-transaction.model';
import { ALL_LIST_FILTER } from '../../../entity-data/entity-data.data';
import { IMasterListItemAction } from '../../../entity-master/components/master-list-item/master-list-item.interface';
import { IMasterListHeader } from '../../../entity-master/components/master-list/master-list.interface';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { IInvoiceDataDispatcher } from '../../../invoice-data/invoice-data-dispatcher.interface';
import { NavigationService } from '../../../navigation/navigation.service';
import {
  IAddManualPayment,
  IInvalidEmailConfirmDialogData,
  ISendReceipt,
  ISendReceiptPreview,
  TransactionActionsEnum,
} from '../../../payments-data/payments-data.data';
import { PaymentsDataDispatcher } from '../../../payments-data/payments-data.dispatcher';
import { DIALOG_TYPE as manualPaymentDialog } from '../../../payments/components/manual-payment-dialog/manual-payment-dialog.component';
import {
  DIALOG_TYPE as sendReceiptDialog,
  ISendReceiptDialogData,
} from '../../../payments/components/send-receipt-dialog/send-receipt-dialog.component';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';
import { Settings } from '../../../settings-data/settings.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { GlobalHeaderThemesEnum } from '../../../shared/components/global-header/global-header-themes.enum';
import { IRouter } from '../../../shared/interfaces/router.interface';
import { MoneyPipe } from '../../../shared/pipes/money/money.pipe';
import { EmailService } from '../../../shared/services/email/email.service';
import { UrlService } from '../../../shared/services/url/url.service';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { TransactionHistoryBuilder } from './transaction-history.builder';

export interface ITransactionAction {
  action: IMasterListItemAction;
  paymentTransaction: PaymentTransaction;
}

@Component({
  selector: 'app-transaction-history',
  templateUrl: './transaction-history.component.html',
  styleUrls: ['./transaction-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [TransactionHistoryBuilder],
})
export class TransactionHistoryComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() invoiceDataDispatcher: IInvoiceDataDispatcher;
  @Input() documentDataStore: IDocumentStore;
  @Input() featureName: string;
  @Input() masterRoute: string;
  @Input() isNewDocument: boolean;
  @Input() companySettings: CompanySettings;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  invalidEmailDialogId: string;
  addPaymentTrackingAction: ITrackingAction;
  headerTheme: GlobalHeaderThemesEnum;
  headers: List<IMasterListHeader>;
  goBackRoute$: Observable<IRouter>;
  clientName$: Observable<string>;
  documentId$: Observable<string>;
  documentInfo$: Observable<string>;
  documentBalance$: Observable<string>;
  documentPaymentTransactions$: Observable<List<PaymentTransaction>>;
  documentCurrencyCode$: Observable<string>;
  documentLocale$: Observable<string>;
  document$: Observable<Document>;
  documentBilling$: Observable<Billing>;
  documentPaymentUrl$: Observable<string>;
  documentCalculationResult$: Observable<CalculationResult>;
  isAllAccess$: Observable<boolean>;
  documentTrackingObject$: Observable<ITrackingObject>;
  isKicEnablingPayment$: BehaviorSubject<boolean>;
  underwritingUrl$: Observable<string>;

  constructor(
    private _transactionHistoryBuilder: TransactionHistoryBuilder,
    private _paymentsDataDispatcher: PaymentsDataDispatcher,
    private _trackingDispatcher: TrackingDispatcher,
    private _clientDataDispatcher: ClientDataDispatcher,
    private _featuresDataStore: FeaturesDataStore,
    private _settingsDataStore: SettingsDataStore,
    private _translateService: TranslateService,
    private _dialogService: DialogService,
    private _moneyPipe: MoneyPipe,
    private _globalBannerService: GlobalBannerService,
    private _navigationService: NavigationService,
    private _urlService: UrlService,
    private _dateTimeService: DateTimeService,
    private _emailService: EmailService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this.headerTheme = GlobalHeaderThemesEnum.action_packed;
    this.invalidEmailDialogId = v4();
    this.headers = this._transactionHistoryBuilder.buildHeaders();
    this.addPaymentTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'add_payment',
      this.trackingScreenName
    );

    this.isKicEnablingPayment$ = new BehaviorSubject<boolean>(false);

    this.isAllAccess$ = this._featuresDataStore.isAllAccess(this.featureName);

    const validDocument: Observable<Document> = this.documentDataStore.getValidEditingEntity();

    this.document$ = validDocument;

    this.documentId$ = validDocument.pipe(
      map((document: Document) => document.id),
      distinctUntilChanged()
    );

    this.documentBilling$ = validDocument.pipe(
      map((document: Document) => document.billing),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentInfo$ = validDocument.pipe(
      map((document: Document) =>
        this._translateService.instant(
          `TRANSACTION_HISTORY.${document.documentType}_INFO`.toUpperCase(),
          { documentNumber: document.docNumber }
        )
      ),
      distinctUntilChanged()
    );

    this.documentCurrencyCode$ = validDocument.pipe(
      map((document: Document) => document.settings.currencyCode),
      distinctUntilChanged()
    );

    this.documentLocale$ = validDocument.pipe(
      map((document: Document) => document.settings.locale),
      distinctUntilChanged()
    );

    this.documentCalculationResult$ = validDocument.pipe(
      map((document: Document) => document.calculationResult),
      distinctUntilChanged()
    );

    this.documentPaymentUrl$ = combineLatest([
      validDocument,
      this._settingsDataStore.settings$,
    ]).pipe(
      map(([document, settings]: [Document, Settings]) => {
        return settings.companySettings.payments.cardPaymentsEnabled() &&
          !document.settings.cardPaymentsDisabled
          ? document.links.electronicPaymentUrl
          : null;
      }),
      distinctUntilChanged()
    );

    this.documentTrackingObject$ = validDocument.pipe(
      map((document: Document) =>
        this._trackingObjectBuilder.buildDocument(
          document.id,
          document.localId,
          document.docNumber,
          document.overallState,
          document.revisionId,
          document.documentType
        )
      )
    );

    this.goBackRoute$ = combineLatest([
      validDocument,
      this.documentTrackingObject$,
    ]).pipe(
      map(([document, documentTrackingObject]: [Document, ITrackingObject]) => {
        const route = Location.joinWithSlash(
          Location.joinWithSlash(
            this.masterRoute,
            document.isNew() || this.isNewDocument
              ? DOCUMENTS_NEW_ROUTE
              : document.id
          ),
          DOCUMENTS_PREVIEW_ROUTE
        );

        return this._transactionHistoryBuilder.buildGoBackRoute(
          route,
          documentTrackingObject,
          this.trackingScreenName
        );
      })
    );

    this.clientName$ = validDocument.pipe(
      map(
        (document: Document) =>
          document.billing.name ||
          this._translateService.instant('TRANSACTION_HISTORY.NO_CLIENT')
      ),
      distinctUntilChanged()
    );

    this.documentPaymentTransactions$ = validDocument.pipe(
      filter((document: Document) => !!document.payments),
      map(
        (document: Document) =>
          document.payments.transactions.sortBy(
            (txn: PaymentTransaction) =>
              -this._dateTimeService.getMilliseconds(txn.datetime)
          ) as List<PaymentTransaction>
      ),
      filter((x) => !!x),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentBalance$ = validDocument.pipe(
      takeUntil(this._destroyed),
      map((document: Document) =>
        this._translateService.instant('TRANSACTION_HISTORY.BALANCE', {
          balanceAmount: this._moneyPipe.transform(
            document.calculationResult.outstandingBalance,
            document.settings.currencyCode,
            document.settings.locale
          ),
        })
      ),
      distinctUntilChanged()
    );

    // for KIC enabling payments
    this.underwritingUrl$ = this._settingsDataStore.getUnderwritingUrl();
  }

  ngAfterViewInit() {
    this._trackingDispatcher.trackPage(this.trackingScreenName);
  }

  refreshTransactions() {
    // it's ok refresh the whole document to get the latest transactions 'cause it has already been saved before
    // the payment dialog is opened
    this.invoiceDataDispatcher.refreshEditingEntity();

    this._clientDataDispatcher.clearList(ALL_LIST_FILTER);
  }

  onAction(transactionAction: ITransactionAction) {
    const action = transactionAction.action;

    switch (action.id) {
      case TransactionActionsEnum.refund_transaction:
        this.refundTransaction(transactionAction.paymentTransaction);
        break;
      case TransactionActionsEnum.delete_transaction:
        this.deleteTransaction(transactionAction.paymentTransaction);
        break;
      case TransactionActionsEnum.unmatch_transaction:
        this.unmatchTransaction(transactionAction.paymentTransaction);
        break;
      case TransactionActionsEnum.send_receipt:
        this.openSendReceiptDialog(transactionAction.paymentTransaction);
        break;
      case TransactionActionsEnum.see_transaction_in_dashboard:
        this.seeTransactionInDashboard(transactionAction.paymentTransaction);
        break;
    }
  }

  openManualPaymentDialog() {
    this.isAllAccess$
      .pipe(
        take(1),
        filter((x) => !!x),
        withLatestFrom(
          this.documentDataStore.getEditingEntity(),
          this.documentDataStore.hasEditingEntityValueChanged(),
          this.documentDataStore.hasEditingEntityBeenSaved()
        ),
        flatMap(
          ([_, document, hasChanges, hasBeenSaved]: [
            boolean,
            Document,
            boolean,
            boolean
          ]) => {
            if (!hasChanges || hasBeenSaved) {
              return of(document);
            }

            this._dialogService.openLoadingDialog();

            return this.invoiceDataDispatcher
              .saveEditingEntity()
              .pipe(finalize(() => this._dialogService.closeLoadingDialog()));
          }
        ),
        takeUntil(this._destroyed),
        finalize(() => this._dialogService.closeLoadingDialog())
      )
      .subscribe(
        () => this._dialogService.openDialog({ type: manualPaymentDialog }),
        (error) => this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  createManualPayment(payload: IAddManualPayment) {
    this.isAllAccess$
      .pipe(
        filter((x) => !!x),
        take(1),
        flatMap(() =>
          this.invoiceDataDispatcher.createManualPayment(payload, true)
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(() => this.invoiceDataDispatcher.refreshEditingEntity());
  }

  deleteTransaction(transaction: PaymentTransaction) {
    this.isAllAccess$
      .pipe(
        take(1),
        filter((x) => !!x),
        takeUntil(this._destroyed)
      )
      .subscribe(
        () => this.invoiceDataDispatcher.deleteManualPayment(transaction),
        (error) => this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  refundTransaction(transaction: PaymentTransaction) {
    this._dialogService.openLoadingDialog();

    this.invoiceDataDispatcher
      .saveEditingEntity()
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe(
        () =>
          this.invoiceDataDispatcher.refundEPayment(transaction.transactionId),
        () => this._dialogService.closeLoadingDialog()
      );
  }

  unmatchTransaction(transaction: PaymentTransaction) {
    this._dialogService.openLoadingDialog();

    this.invoiceDataDispatcher
      .saveEditingEntity()
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe(
        () =>
          this.invoiceDataDispatcher.unmatchTransaction(
            transaction.transactionId
          ),
        () => this._dialogService.closeLoadingDialog()
      );
  }

  openSendReceiptDialog(transaction: PaymentTransaction) {
    if (transaction.isPayment()) {
      this.documentDataStore
        .getEditingEntity()
        .pipe(take(1), takeUntil(this._destroyed))
        .subscribe((document: Document) => {
          const email = document.billingEmail;

          if (!email || !this._emailService.validateEmails(email)) {
            this._dialogService.openConfirmDialog(this.invalidEmailDialogId, {
              documentType: document.documentType,
              documentId: document.id,
            } as IInvalidEmailConfirmDialogData);

            return;
          }

          const dialogData: ISendReceiptDialogData = {
            document,
            transaction,
            paymentsSettings: this.companySettings.payments,
          };

          this._dialogService.openDialog({
            type: sendReceiptDialog,
            data: dialogData,
          });
        });
    }
  }

  sendReceipt({
    documentId,
    documentBilling,
    transactionId,
    renderingOptions,
  }: ISendReceipt) {
    this._paymentsDataDispatcher.sendReceipt(
      documentId,
      documentBilling.name || documentBilling.email,
      transactionId,
      renderingOptions,
      true
    );
  }

  sendPreviewReceipt({
    document,
    transaction,
    renderingOptions,
  }: ISendReceiptPreview) {
    this._paymentsDataDispatcher.sendReceiptPreview(
      document,
      transaction,
      renderingOptions
    );
  }

  seeTransactionInDashboard(transaction: PaymentTransaction) {
    if (transaction.isCreditCardTransaction()) {
      this._dialogService.openLoadingDialog();

      this.documentId$
        .pipe(
          take(1),
          flatMap((documentId: string) =>
            this._paymentsDataDispatcher.getTransactionHistoryUrl(
              documentId,
              transaction.transactionId
            )
          ),
          finalize(() => this._dialogService.closeLoadingDialog()),
          takeUntil(this._destroyed)
        )
        .subscribe(noop, (error) =>
          this._globalBannerService.showSystemErrorBanner(error)
        );
    }
  }

  onInvalidClientEmail({ result, data }: IConfirmDialogResult) {
    const { documentId }: IInvalidEmailConfirmDialogData = data;

    if (result && documentId) {
      this.invoiceDataDispatcher.goToDocumentAddClientScreen(documentId);
    }
  }

  onEnableCardPayments() {
    const currentHref = this._navigationService.getLocationHref();
    const currentUrl = this._urlService.parse(currentHref);
    const currentPath = currentUrl.path;

    this.isKicEnablingPayment$.next(true);

    this.documentDataStore
      .getEditingEntity()
      .pipe(
        take(1),
        flatMap((document: Document) => {
          const newPath = currentPath.replace(
            `/${DOCUMENTS_NEW_ROUTE}`,
            `/${document.id}`
          );
          // doesn't work on localdev 'cause we don't add the port
          const redirectUrl = `${currentUrl.protocol}://${currentUrl.host}${newPath}`;

          return this.invoiceDataDispatcher.enableCardPayments(
            document.id,
            redirectUrl,
            true
          );
        }),
        takeUntil(this._destroyed),
        finalize(() => this.isKicEnablingPayment$.next(false))
      )
      .subscribe(noop, () =>
        this._dialogService.closeDialog({ type: manualPaymentDialog })
      );
  }

  ngOnDestroy() {
    this._dialogService.closeDialog({ type: sendReceiptDialog });
    this._dialogService.closeDialog({ type: manualPaymentDialog });
    this._dialogService.closeConfirmDialog(this.invalidEmailDialogId);
    this._dialogService.closeLoadingDialog();

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
